import { useEffect, useState, useMemo, useRef } from '@max/max';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { useRequest } from './useRequest';
import { ActivityStatus, API_PATH as API, API_PATH, RecordTabType, ACTIVITY_ENDED_CODE, IS_SUBSCRIBED_CACHE_KEY, MARKETING_ID_TYPE } from '../types/shared';
import { resolveActivityViewModel, resolveFreeCard, checkIsHaveFreeCardUsed, resolveNoticeItems } from '../utils/resolve';
import { parseFreeOrderCardExtra } from '../utils/rewardRecords';
import { getRequestBaseParams } from '../utils/request';
import { useAssistAction } from './useAssistAction';
import { useQueryPopupCache } from './useQueryPopupCache';
import { useJoinActivityAction } from './useJoinActivityAction';
import { useLeaderboardNotice } from './useLeaderboardNotice';
import { useActivityCache } from './useActivityCache';
import { useCardGroupDetail } from './useCardGroupDetail';
const isMRN = false;
import msi from "../utils/msi/index.web";
import { useShare } from "./useShare/useShare.web";
import { usePageVisibility } from "./usePageVisibility/usePageVisibility.web";
import { useTaskCenter } from './useTaskCenter';
import { subscribeMockScene } from '../mock';
import { mergePopupMapPreserving } from '../utils/taskResolve';
const CITY_EXPLORE_SCENE = 1;
const IS_SHOW_COUPON_POPUP_CACHE_KEY = 'isShowCouponPopup';
const IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY = 'isShowFreeOrderPopup';
const CACHE_EXPIRE_TIME = 15552000; // 15,552,000 ms = 180 days
// R004：日历相关常量（参考 mcp-tex-periodic-reward-claim）
const CALENDAR_SCENE_TOKEN = 'dd-2feed6b01d76054a'; // hotel 类组件统一 sceneToken
const ALARM_OFFSET_SECONDS = 600; // 提前 10 分钟提醒
const ALARM_OFFSET_MS = ALARM_OFFSET_SECONDS * 1000;
const POLLING_INTERVAL = 10000;
export var PopupKey;
(function (PopupKey) {
    PopupKey["CITY_SELECT_MODAL"] = "citySelectModal";
    PopupKey["GUEST_ASSIST_POPUP"] = "guestAssistPopup";
    PopupKey["GET_FREE_ORDER_POPUP"] = "getFreeOrderPopup";
    PopupKey["GET_COUPON_POPUP"] = "getCouponPopup";
    PopupKey["LANDMARK_DETAIL_MODAL"] = "landmarkDetailModal";
    PopupKey["REWARD_RECORDS"] = "rewardRecords";
    PopupKey["BINDING_RULE_MODAL"] = "bindingRuleModal";
    // R002
    PopupKey["WINNER_LIST_MODAL"] = "winnerListModal";
    // R004
    PopupKey["TASK_LIST_MODAL"] = "taskListModal"; // R007：任务半页弹窗
})(PopupKey || (PopupKey = {}));
const defaultPopupMap = {
    [PopupKey.CITY_SELECT_MODAL]: false,
    [PopupKey.GUEST_ASSIST_POPUP]: false,
    [PopupKey.GET_FREE_ORDER_POPUP]: false,
    [PopupKey.GET_COUPON_POPUP]: false,
    [PopupKey.LANDMARK_DETAIL_MODAL]: false,
    [PopupKey.REWARD_RECORDS]: false,
    [PopupKey.BINDING_RULE_MODAL]: false,
    [PopupKey.WINNER_LIST_MODAL]: false,
    [PopupKey.TASK_LIST_MODAL]: false // R007
};
export function checkIsHaveFreeCard(beRes) {
    try {
        const freeOrder = resolveFreeCard(beRes);
        return !!freeOrder;
    }
    catch (error) {
        return false;
    }
}
export function checkIsHaveCoupon(beRes) {
    try {
        if (Array.isArray(beRes?.rewardItems) && beRes.rewardItems.length > 0) {
            return true;
        }
        return false;
    }
    catch (error) {
        return false;
    }
}
export function checkIsJoined(beRes) {
    const selectedGroups = beRes?.moduleInfoList?.[0]?.cardModuleInfoVO?.selectedCardGroups || [];
    return Array.isArray(selectedGroups) && selectedGroups.length > 0;
}
// R003：调整判断优先级，isHaveFreeCard > isJoined
// R004 的 ENDED 判断已在 init() 外层通过 raw status 提前处理，此函数不再负责 ENDED
export function getInitActivityStatus(queryInfoRes, queryRewardsRes) {
    // R003：优先判断免单卡，跨周期用户 isJoined=false 时也直接展示 HAVE_FREE_CARD
    const isHaveFreeCard = checkIsHaveFreeCard(queryRewardsRes);
    if (isHaveFreeCard) {
        const isUsed = checkIsHaveFreeCardUsed(queryRewardsRes);
        return isUsed ? ActivityStatus.HAVE_FREE_CARD_USED : ActivityStatus.HAVE_FREE_CARD_UNUSED;
    }
    const isJoined = checkIsJoined(queryInfoRes);
    if (!isJoined)
        return ActivityStatus.UNJOINED;
    return ActivityStatus.ON_PROGRESS;
}
export function useCityExploreData(options) {
    const ctx = useMonitorEngineContext();
    const commonParams = ctx._runtimeInfo?.commonParams ?? {};
    const { userId } = ctx._runtimeInfo?.commonParams || {};
    const { leaderId } = ctx._runtimeInfo?.queryParams || {};
    const currentActivityId = options.feConfig.activityId;
    const { shareWXCard } = useShare();
    // R001：好友助力分享，供 handleConfirmJoin 成功后调用，以及外层邀请按钮复用
    function cityExploreShare() {
        shareWXCard({
            params: {
                taskId: options.taskId,
                leaderId: userId,
                isFromShare: 'true'
            },
            shareConfig: options.feConfig.shareConfig,
            onSuccess: () => { }
        });
    }
    const { onTriggerAssistAction } = useAssistAction({
        feConfig: options.feConfig,
        activityId: currentActivityId,
        taskId: options.taskId
    });
    const { checkPopupCache } = useQueryPopupCache({
        activityId: currentActivityId
    });
    const { onTriggerJoinActivity } = useJoinActivityAction({
        activityId: currentActivityId
    });
    // 榜单数据由 hook 统一管理，不再在 NoticeSlider 内部请求
    const { leaderboardItems, canBindBizLines, loadLeaderboardData } = useLeaderboardNotice({
        activityId: currentActivityId
    });
    // R001：3.14 缓存操作由独立 hook 管理
    // R001：3.9 集卡分组详情查询，只暴露请求函数，状态由本 hook 管理
    const { loadCardGroupDetail } = useCardGroupDetail({
        activityId: currentActivityId
    });
    const { readPreSelectedCityCache, writePreSelectedCityCache, clearPreSelectedCityCache, readCache, writeCache } = useActivityCache({
        activityId: currentActivityId
    });
    // 免单卡状态轮询定时器 ref，避免重复创建
    const rewardPollingTimerRef = useRef(null);
    // state 声明
    const [activityStatus, setActivityStatus] = useState(ActivityStatus.LOADING);
    const [rawQueryInfoResponse, setRawQueryInfoResponse] = useState(null);
    const [rawQueryRewardsResponse, setRawQueryRewardsResponse] = useState(null);
    const [popupMap, setPopupMap] = useState(defaultPopupMap);
    // R007：popupMap 最新值镜像，供 useTaskCenter Toast 仲裁读取（避免闭包旧值）
    const popupMapRef = useRef(popupMap);
    popupMapRef.current = popupMap;
    const [selectedLandmark, setSelectedLandmark] = useState(null);
    const [selectedRewardTabType, setSelectedRewardTabType] = useState(RecordTabType.REWARD);
    // R001：预选城市，用户在城市弹窗选择后暂存，等待确认，需要驱动 PRE_JOINED 卡片渲染
    const [preSelectedCity, setPreSelectedCity] = useState(null);
    // R001：PRE_JOINED 态通过 3.9 接口查询到的预选城市地标列表
    const [preJoinedLandmarks, setPreJoinedLandmarks] = useState([]);
    // R004：是否已预约下次活动，驱动 ENDED 卡片按钮状态
    const [isSubscribed, setIsSubscribed] = useState(false);
    // R001：是否有助力记录，ON_PROGRESS 态 init 后异步更新，false 时展示角标
    const [hasAssistRecord, setHasAssistRecord] = useState(true);
    // R001：重选城市时跳过城市弹窗入场动画，由 handleReSelectCity 置为 true，弹窗关闭后重置
    const [skipCityModalAnimation, setSkipCityModalAnimation] = useState(false);
    // R004：raw: true 模式，保留外层 status，用于识别 ACTIVITY_ENDED_CODE
    const { requestStatus: queryInfoStatus, request: queryInfo } = useRequest(API.QUERY_INFO, {
        raw: true
    });
    const { requestStatus: queryRewardsStatus, request: queryRewards } = useRequest(API_PATH.REWARDS);
    const { request: queryPopupCache } = useRequest(API_PATH.QUERY_POPUP_CACHE);
    const { request: queryAppointment } = useRequest(API_PATH.QUERY_APPOINTMENT);
    const { request: setAppointment } = useRequest(API_PATH.SET_APPOINTMENT);
    const { request: queryAssistRecord } = useRequest(API_PATH.ASSIST_RECORD);
    async function resolveInitStatus(queryInfoRaw, queryRewardsRes, feCommonParams) {
        // 最高优先级：持有免单卡（R003），无论活动是否结束都展示免单卡态
        // 将原始活动信息数据传入，由函数内部解析 isEnded，确保免单卡判断始终在最前
        const isHaveFreeCard = checkIsHaveFreeCard(queryRewardsRes);
        if (isHaveFreeCard) {
            const isUsed = checkIsHaveFreeCardUsed(queryRewardsRes);
            return {
                status: isUsed ? ActivityStatus.HAVE_FREE_CARD_USED : ActivityStatus.HAVE_FREE_CARD_UNUSED
            };
        }
        // 次优先级：活动已结束（R004），并行读 3.14 缓存 + 3.15 预约状态
        const isEnded = queryInfoRaw?.status === ACTIVITY_ENDED_CODE;
        const queryInfoRes = queryInfoRaw?.status === 0 ? queryInfoRaw.data : null;
        if (isEnded) {
            const [subscribeCache, appointmentRes] = await Promise.all([readCache(IS_SUBSCRIBED_CACHE_KEY).catch(() => null), queryAppointment({
                    marketingId: currentActivityId,
                    marketingIdType: MARKETING_ID_TYPE,
                    appointmentTime: options.feConfig.nextActivityConfig?.appointmentTime ? new Date(options.feConfig.nextActivityConfig.appointmentTime).getTime() : 0,
                    ...feCommonParams
                }).catch(() => null)]);
            return {
                status: ActivityStatus.ENDED,
                isSubscribed: !!subscribeCache || appointmentRes?.success === true
            };
        }
        // 其余状态：isJoined 判断（UNJOINED 时读缓存决定是否升级为 PRE_JOINED，edge 3）
        const baseStatus = getInitActivityStatus(queryInfoRes ?? null, queryRewardsRes);
        if (baseStatus === ActivityStatus.UNJOINED) {
            const cachedCity = await readPreSelectedCityCache();
            if (cachedCity) {
                return {
                    status: ActivityStatus.PRE_JOINED,
                    preSelectedCity: cachedCity
                };
            }
        }
        return {
            status: baseStatus
        };
    }
    async function getInitPopupMap(isRefresh, isGuest, status, queryInfoRes, queryRewardsRes, assistRes) {
        const isHaveFreeCard = checkIsHaveFreeCard(queryRewardsRes);
        const isHaveCoupon = checkIsHaveCoupon(queryRewardsRes);
        if (isGuest && assistRes) {
            return {
                ...defaultPopupMap,
                [PopupKey.GUEST_ASSIST_POPUP]: true
            };
        }
        else if (status === ActivityStatus.UNJOINED) {
            // 只有明确是 UNJOINED 才弹城市选择，PRE_JOINED/ENDED/HAVE_FREE_CARD 均不触发
            return {
                ...defaultPopupMap,
                [PopupKey.CITY_SELECT_MODAL]: isRefresh ? false : true
            };
        }
        else if (isHaveCoupon || isHaveFreeCard) {
            const popupCacheRes = await checkPopupCache(true, {
                [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
            });
            if (popupCacheRes) {
                if (isHaveFreeCard && !popupCacheRes?.[IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]) {
                    await checkPopupCache(false, {
                        [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                        [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
                    });
                    return {
                        ...defaultPopupMap,
                        [PopupKey.GET_FREE_ORDER_POPUP]: true
                    };
                }
                else if (isHaveCoupon && !popupCacheRes?.[IS_SHOW_COUPON_POPUP_CACHE_KEY]) {
                    await checkPopupCache(false, {
                        [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                        [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
                    });
                    return {
                        ...defaultPopupMap,
                        [PopupKey.GET_COUPON_POPUP]: true
                    };
                }
            }
        }
        return defaultPopupMap;
    }
    async function init(isRefresh = false) {
        try {
            const isGuestMode = !!leaderId;
            const feCommonParams = await getRequestBaseParams(commonParams);
            const queryInfoParams = {
                activityId: currentActivityId,
                scene: CITY_EXPLORE_SCENE,
                ...feCommonParams
            };
            // R003：3.11 奖励查询接口将当前活动 id 与历史活动 id 列表合并，支持跨周期查询
            const queryRewardsParams = {
                activityIds: [currentActivityId, ...(options.feConfig.historyActivityIds ?? [])],
                ...feCommonParams
            };
            const [queryInfoRaw, queryRewardsRes, queryAssistRes] = await Promise.all([queryInfo(queryInfoParams), queryRewards(queryRewardsParams).catch(() => {
                    return null;
                }), new Promise(resolve => {
                    if (isGuestMode) {
                        onTriggerAssistAction().then(data => {
                            resolve(data);
                        }).catch(() => {
                            resolve(null);
                        });
                    }
                    else {
                        resolve(null);
                    }
                })]);
            // R004：识别活动结束，非 0 且非 ENDED 视为接口异常
            const isEnded = queryInfoRaw?.status === ACTIVITY_ENDED_CODE;
            if (!isEnded && queryInfoRaw?.status !== 0) {
                throw new Error(`queryInfo 返回非0：status=${queryInfoRaw?.status}`);
            }
            const queryInfoRes = queryInfoRaw?.status === 0 ? queryInfoRaw.data : null;
            setRawQueryInfoResponse(queryInfoRes);
            setRawQueryRewardsResponse(queryRewardsRes);
            const { status, preSelectedCity: cachedCity, isSubscribed: subscribed } = await resolveInitStatus(queryInfoRaw, queryRewardsRes, feCommonParams);
            setActivityStatus(status);
            if (cachedCity)
                setPreSelectedCity(cachedCity);
            if (subscribed !== undefined)
                setIsSubscribed(subscribed);
            // R001 edge 3：从缓存恢复 PRE_JOINED，同样需要请求地标（packId 来自 3.5 接口 CardModuleInfoVO.packId）
            if (status === ActivityStatus.PRE_JOINED && cachedCity) {
                const packId = queryInfoRes?.moduleInfoList?.[0]?.cardModuleInfoVO?.packId;
                if (packId != null) {
                    loadCardGroupDetail(packId, cachedCity.id?.toString() ?? '').then(setPreJoinedLandmarks).catch(() => { });
                }
            }
            // ENDED 态不需要弹窗逻辑
            if (status !== ActivityStatus.ENDED) {
                const initPopupMap = await getInitPopupMap(isRefresh, isGuestMode, status, queryInfoRes, queryRewardsRes, queryAssistRes);
                // R007：整图替换改 merge —— 保留任务半页弹窗当前显隐，否则浏览完成触发的刷新会把打开着的任务弹窗关掉
                setPopupMap(prev => mergePopupMapPreserving(initPopupMap, prev, [PopupKey.TASK_LIST_MODAL]));
            }
            // R001：ON_PROGRESS 态串行请求助力记录，判断是否为空，不阻塞主流程渲染
            if (status === ActivityStatus.ON_PROGRESS) {
                queryAssistRecord({
                    activityId: currentActivityId,
                    leaderId: String(userId),
                    pageSize: 1,
                    ...feCommonParams
                }).then(res => {
                    setHasAssistRecord(Array.isArray(res?.records) && res.records.length > 0);
                }).catch(() => {
                    // 请求失败时保守处理：不展示角标，避免误导用户
                    setHasAssistRecord(true);
                });
            }
        }
        catch (error) {
            setActivityStatus(ActivityStatus.FAILED);
            ctx._uiInteractionAPI.toast.show({
                content: '获取数据失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API.QUERY_INFO
            });
            ctx._raptorReport.monitorReportLog(error.message, error, {
                category: 'ajaxError',
                level: 'error'
            });
        }
    }
    // R001：edge 14 handler，点击城市进入 PRE_JOINED（不调 joinActivity）
    // 流程：① await 写缓存（失败则 toast 提示并阻断）→ ② await 请求地标 → ③ 一次性更新所有 state 流转到 PRE_JOINED
    async function handlePreSelectCity(cityInfo) {
        // ① 写缓存，失败则提示网络异常并阻断流程
        try {
            await writePreSelectedCityCache(cityInfo);
        }
        catch {
            ctx._uiInteractionAPI.toast.show({
                content: '网络异常，请稍后重试'
            });
            return;
        }
        // ② 请求预选城市地标（packId 来自 3.5 接口 CardModuleInfoVO.packId）
        let landmarks = [];
        const packId = rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.packId;
        if (packId != null) {
            try {
                landmarks = await loadCardGroupDetail(packId, cityInfo.id?.toString() ?? '');
            }
            catch {
                ctx._uiInteractionAPI.toast.show({
                    content: '网络异常，请稍后重试'
                });
                return;
            }
        }
        // ③ 数据就绪，一次性更新 state，UI 完整渲染 PRE_JOINED 卡片
        setPreSelectedCity(cityInfo);
        setPreJoinedLandmarks(landmarks);
        setActivityStatus(ActivityStatus.PRE_JOINED);
        setPopupMap(defaultPopupMap);
    }
    // R001：edge 15 handler，锁定城市，真正调用 joinActivity
    async function handleConfirmJoin() {
        if (!preSelectedCity)
            return;
        try {
            await onTriggerJoinActivity(preSelectedCity.id?.toString() ?? '');
            // 参与成功后立即触发分享，不等 init 完成
            cityExploreShare();
            // 重跑 init：刷新 3.5 + 3.11、重走弹窗逻辑、检查助力记录角标，状态完全对齐
            init(true);
        }
        catch (error) {
            ctx._uiInteractionAPI.toast.show({
                content: '参与活动失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API_PATH.JOIN_ACTIVITY
            });
            ctx._raptorReport.monitorReportLog?.('joinActivity', error, {
                level: 'error',
                category: 'ajaxError'
            });
        }
    }
    // R001：edge 16 handler，重选城市，只打开城市弹窗
    // preSelectedCity 和缓存保持不变，用户重新点城市后 edge 14 再次触发覆盖
    function handleReSelectCity() {
        setSkipCityModalAnimation(true);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.CITY_SELECT_MODAL]: true
        });
    }
    function handleCloseAssistModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GUEST_ASSIST_POPUP]: false,
            [PopupKey.CITY_SELECT_MODAL]: activityStatus === ActivityStatus.UNJOINED
        });
    }
    function handleClickLandmarkItem(landmarkVo) {
        setSelectedLandmark(landmarkVo);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.LANDMARK_DETAIL_MODAL]: true
        });
        // 城市信息按状态分三个分支取值：
        // - PRE_JOINED：来自 preSelectedCity（selectedCardGroups 为空，不能从 rawQueryInfoResponse 取）
        // - HAVE_FREE_CARD_UNUSED/USED：来自 FreeOrderCardExtra.selectedCardGroups[0]
        // - 其他（ON_PROGRESS 等）：来自 rawQueryInfoResponse.cardModuleInfoVO
        let cityId = 0;
        let cityName = '';
        if (activityStatus === ActivityStatus.PRE_JOINED) {
            cityId = preSelectedCity?.id ?? 0;
            cityName = preSelectedCity?.name ?? '';
        }
        else if (activityStatus === ActivityStatus.HAVE_FREE_CARD_UNUSED || activityStatus === ActivityStatus.HAVE_FREE_CARD_USED) {
            const freeCardExtra = parseFreeOrderCardExtra(freeOrder);
            cityId = freeCardExtra?.selectedCardGroups?.[0]?.cityId || 0;
            cityName = freeCardExtra?.selectedCardGroups?.[0]?.cityName || '';
        }
        else {
            cityId = viewModel?.currentCityId || 0;
            cityName = viewModel?.currentCityName || '全国';
        }
        // 发送三个事件，参照 trip-around-recommend-guide 的实现
        ctx?._communication?.emit?.('travelAroundGuideChange', {
            playName: landmarkVo.name || '',
            playUnifyIdString: landmarkVo.cardId || '',
            productInfoList: [],
            cityId
        });
        ctx?._communication?.emit?.('dynamicPoiListSearchParamsChanged', {
            landmark: landmarkVo.name || '',
            cityId,
            lat: landmarkVo.cardExtra?.latitude,
            lng: landmarkVo.cardExtra?.longitude
        });
        ctx?._communication?.emit?.('cityChanged', {
            value: cityId,
            title: cityName
        });
    }
    function handleCloseLandmarkDetailModal() {
        setSelectedLandmark(null);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.LANDMARK_DETAIL_MODAL]: false
        });
    }
    function handleCloseCouponModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GET_COUPON_POPUP]: false
        });
    }
    function handleCloseRewardRecords() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.REWARD_RECORDS]: false
        });
    }
    function handleSelectRewardTabType(tabType) {
        setSelectedRewardTabType(tabType);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.REWARD_RECORDS]: true
        });
    }
    function handleCloseFreeCardModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GET_FREE_ORDER_POPUP]: false
        });
    }
    function handleCloseCityModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.CITY_SELECT_MODAL]: false
        });
        // 关闭后重置，下次正常打开仍走 isFirstOpenRef 逻辑
        // setSkipCityModalAnimation(false);
    }
    function handleJoinActivity() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.CITY_SELECT_MODAL]: true
        });
    }
    // R002：edge 35 handler，打开绑定规则弹窗
    function handleOpenBindingRuleModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.BINDING_RULE_MODAL]: true
        });
    }
    // R002：edge 36 handler，关闭绑定规则弹窗
    function handleCloseBindingRuleModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.BINDING_RULE_MODAL]: false
        });
    }
    // R004：edge 37 handler，打开中奖名单弹窗
    function handleOpenWinnerList() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.WINNER_LIST_MODAL]: true
        });
    }
    // R004：edge 38 handler，关闭中奖名单弹窗
    function handleCloseWinnerList() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.WINNER_LIST_MODAL]: false
        });
    }
    // R004：edge 39 handler，预约下次活动
    // 流程：MRN 端优先写日历，成功后写缓存（不调 3.16）；
    //       日历失败（含拒绝授权）或 Web 端，降级调 3.16，成功后写缓存
    async function handleSubscribeNextActivity() {
        const nextActivityConfig = options.feConfig.nextActivityConfig ?? {};
        // appointmentTime 未配置时不执行预约
        if (!nextActivityConfig.appointmentTime)
            return;
        // DateSetter 返回 'yyyy-MM-dd HH:mm:ss' 字符串，转为毫秒时间戳
        const appointmentTime = new Date(nextActivityConfig.appointmentTime).getTime();
        const feCommonParams = await getRequestBaseParams(commonParams);
        // 写缓存 + 更新 state 的公共逻辑
        async function markSubscribed() {
            writeCache(IS_SUBSCRIBED_CACHE_KEY, '1').catch(() => null);
            setIsSubscribed(true);
        }
        // 降级路径：调 3.16 后端预约接口（携带 reachId 触发短信/push 触达）
        // 注：不写 3.14 缓存，后端已通过 3.16 记录预约状态，init 时由 3.15 接口恢复
        async function fallbackToSetAppointment() {
            try {
                await setAppointment({
                    marketingId: currentActivityId,
                    marketingIdType: MARKETING_ID_TYPE,
                    appointmentTime,
                    reachId: nextActivityConfig.reachId ?? '123',
                    realReachTime: appointmentTime - ALARM_OFFSET_MS,
                    ...feCommonParams
                });
                setIsSubscribed(true);
                ctx._uiInteractionAPI.toast.show({
                    content: '预约成功'
                });
            }
            catch (error) {
                ctx._uiInteractionAPI.toast.show({
                    content: '预约失败，请稍后重试'
                });
                ctx._raptorReport.monitorReportLog?.('setAppointment', error, {
                    level: 'error',
                    category: 'ajaxError'
                });
            }
        }
        if (isMRN) {
            // MRN 端：优先写日历，失败降级调 3.16
            const startTimeSec = Math.floor(appointmentTime / 1000);
            try {
                await new Promise((resolve, reject) => {
                    msi?.authorize?.({
                        scope: 'scope.calendarWrite',
                        _mt: {
                            sceneToken: CALENDAR_SCENE_TOKEN
                        },
                        success: () => {
                            msi?.addPhoneCalendar?.({
                                _mt: {
                                    sceneToken: CALENDAR_SCENE_TOKEN
                                },
                                title: nextActivityConfig.title ?? '美团抽免单',
                                description: nextActivityConfig.description ?? '美团抽免单活动即将开始',
                                startTime: startTimeSec,
                                endTime: startTimeSec + 3600,
                                alarm: true,
                                alarmOffset: ALARM_OFFSET_SECONDS,
                                switchToCalendar: false,
                                success: () => resolve(),
                                fail: (e) => reject(e)
                            });
                        },
                        fail: (e) => reject(e)
                    });
                });
                // 日历写入成功：写缓存，不调 3.16
                await markSubscribed();
                ctx._uiInteractionAPI.toast.show({
                    content: '预约成功，已添加到日历提醒'
                });
            }
            catch (e) {
                // 日历写入失败（含用户拒绝授权）：降级调 3.16
                ctx._raptorReport.monitorReportLog?.('addPhoneCalendar', e, {
                    level: 'warn',
                    category: 'jsError'
                });
                await fallbackToSetAppointment();
            }
        }
        else {
            // Web 端：不支持日历，直接走 3.16 降级路径
            await fallbackToSetAppointment();
        }
    }
    // 免单卡状态轮询：activityStatus 为免单卡态时按运营配置间隔请求 3.11 奖励接口，
    // 更新免单卡使用状态（UNUSED/USED）和里程碑进度；离开免单卡态或组件销毁时清除定时器
    useEffect(() => {
        const pollingInterval = options.feConfig.haveCardUnusedConfig?.rewardPollingInterval;
        const isFreeCardStatus = activityStatus === ActivityStatus.HAVE_FREE_CARD_UNUSED || activityStatus === ActivityStatus.HAVE_FREE_CARD_USED;
        // 非免单卡态或未配置间隔时，清除已有定时器并退出
        if (!isFreeCardStatus || !pollingInterval) {
            if (rewardPollingTimerRef.current !== null) {
                clearInterval(rewardPollingTimerRef.current);
                rewardPollingTimerRef.current = null;
            }
            return;
        }
        // 已有定时器时不重复创建（activityStatus 在免单卡两个子态之间切换时保持同一个 timer）
        if (rewardPollingTimerRef.current !== null)
            return;
        const realPollingInterval = pollingInterval < POLLING_INTERVAL ? POLLING_INTERVAL : pollingInterval;
        rewardPollingTimerRef.current = setInterval(async () => {
            try {
                const feCommonParams = await getRequestBaseParams(commonParams);
                const res = await queryRewards({
                    activityIds: [currentActivityId, ...(options.feConfig.historyActivityIds ?? [])],
                    ...feCommonParams
                });
                setRawQueryRewardsResponse(res);
                // 根据最新奖励数据同步更新免单卡使用状态
                if (checkIsHaveFreeCard(res)) {
                    const isUsed = checkIsHaveFreeCardUsed(res);
                    setActivityStatus(isUsed ? ActivityStatus.HAVE_FREE_CARD_USED : ActivityStatus.HAVE_FREE_CARD_UNUSED);
                }
            }
            catch {
                // 轮询请求失败静默处理，不影响主流程
            }
        }, realPollingInterval);
        return () => {
            if (rewardPollingTimerRef.current !== null) {
                clearInterval(rewardPollingTimerRef.current);
                rewardPollingTimerRef.current = null;
            }
        };
    }, [activityStatus, options.feConfig.haveCardUnusedConfig?.rewardPollingInterval]);
    // R001：PRE_JOINED 态 landmarks 覆盖为 preJoinedLandmarks（3.9 接口返回）
    const viewModel = useMemo(() => {
        const base = resolveActivityViewModel(rawQueryInfoResponse);
        if (activityStatus === ActivityStatus.PRE_JOINED) {
            return {
                ...base,
                landmarks: preJoinedLandmarks
            };
        }
        return base;
    }, [rawQueryInfoResponse, activityStatus, preJoinedLandmarks]);
    const freeOrder = useMemo(() => {
        return resolveFreeCard(rawQueryRewardsResponse);
    }, [rawQueryRewardsResponse]);
    // noticeItems 由 resolveNoticeItems 纯函数派生，activityStatus/feConfig 变化时自动重算
    const noticeItems = useMemo(() => resolveNoticeItems(activityStatus, leaderboardItems, canBindBizLines, options.feConfig), [activityStatus, leaderboardItems, canBindBizLines]);
    useEffect(() => {
        try {
            init();
        }
        catch (error) {
            ctx._uiInteractionAPI.toast.show({
                content: '获取数据失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API.QUERY_INFO
            });
            ctx._raptorReport.monitorReportLog(error.message, error, {
                category: 'ajaxError',
                level: 'error'
            });
        }
    }, []);
    // 开发期 mock 场景切换：监听场景变更后整体重新拉取（init 覆盖 3.5/奖励/助力等，榜单单独重拉以更新 canBindBizLines）。
    // 用 ref 持最新函数，订阅只注册一次。非 mock 模式无人触发，零副作用。
    const initRef = useRef(init);
    initRef.current = init;
    const reloadLeaderboardRef = useRef(loadLeaderboardData);
    reloadLeaderboardRef.current = loadLeaderboardData;
    useEffect(() => subscribeMockScene(() => {
        // 先重置弹窗（清掉上个场景残留的弹窗，ENDED 态 init 不走弹窗逻辑，否则会残留城市弹窗），
        // 再完整 init（isRefresh=false）以复现进入该场景的首屏行为（未参与自动弹城市选择、奖励弹窗按缓存等）。
        setPopupMap(defaultPopupMap);
        reloadLeaderboardRef.current();
        initRef.current();
    }), []);
    // R007：任务半页弹窗编排（浏览/下单任务）
    const taskCenter = useTaskCenter({
        feConfig: options.feConfig,
        rawQueryInfoResponse,
        refresh: () => init(true),
        getPopupMap: () => popupMapRef.current,
        setTaskModalVisible: (visible) => setPopupMap(prev => ({
            ...prev,
            [PopupKey.TASK_LIST_MODAL]: visible
        })),
        rewardPopupKeys: [PopupKey.GET_FREE_ORDER_POPUP, PopupKey.GET_COUPON_POPUP],
        // 跳列表页携带「用户选择探索的城市」，而非设备定位城市
        selectedCityId: viewModel?.currentCityId
    });
    // R007：回前台/导航返回统一刷新一次 init；刷新后 rawQueryInfoResponse 变化会触发 useTaskCenter 内的
    // 缓存对账（checkAndCompleteDueBrowseTasks）自动完成到点的浏览任务，故这里只需 init(true)。
    usePageVisibility(() => {
        init(true);
    });
    return {
        taskCenter,
        activityStatus,
        viewModel,
        rawQueryInfoResponse,
        rawQueryRewardsResponse,
        popupMap,
        selectedLandmark,
        selectedRewardTabType,
        freeOrder,
        preSelectedCity,
        isSubscribed,
        skipCityModalAnimation,
        noticeItems,
        canBindBizLines,
        hasAssistRecord,
        cityExploreShare,
        handlePreSelectCity,
        handleConfirmJoin,
        handleReSelectCity,
        handleSelectRewardTabType,
        handleCloseAssistModal,
        handleClickLandmarkItem,
        handleCloseLandmarkDetailModal,
        handleCloseCouponModal,
        handleCloseRewardRecords,
        handleCloseFreeCardModal,
        handleCloseCityModal,
        handleJoinActivity,
        handleSubscribeNextActivity,
        handleOpenWinnerList,
        handleCloseWinnerList,
        handleOpenBindingRuleModal,
        handleCloseBindingRuleModal
    };
}
